import type {
  ApiResponseBody,
  ApiSearchQuery,
} from "@/lib/api/api-type-helper";
import type { EnumUnion } from "@dresscode-frontend/utility-types";

// API型定義
export type NetworkQuery = ApiSearchQuery<"/v2/networks", "get">;
export type NetworkListResponse = ApiResponseBody<"/v2/networks", "get">;
export type NetworkItem = NetworkListResponse["items"][number];
export type NetworkCountResponse = ApiResponseBody<"/v2/networks-count", "get">;
export type NetworkDetailResponse = ApiResponseBody<"/v2/networks/{id}", "get">;

// ネットワークソート順
export const NetworkOrderBy = {
  ID: "ID",
  NETWORK_NAME: "NETWORK_NAME",
  CONNECTION_TYPE: "CONNECTION_TYPE",
  NETWORK_TYPE: "NETWORK_TYPE",
  PURPOSE: "PURPOSE",
} as const satisfies Record<NonNullable<NetworkQuery["orderBy"]>, string>;
export type NetworkOrderBy = EnumUnion<typeof NetworkOrderBy>;

// ネットワークタイプ
export const NetworkTypeEnum = {
  INTERNAL: "INTERNAL",
  EXTERNAL: "EXTERNAL",
  VPN: "VPN",
  DEDICATED_LINE: "DEDICATED_LINE",
  CLOUD: "CLOUD",
} as const satisfies Record<string, NonNullable<NetworkItem["networkType"]>>;
export type NetworkType = EnumUnion<typeof NetworkTypeEnum>;

// 接続タイプ
export const ConnectionTypeEnum = {
  WIRED: "WIRED",
  WIRELESS: "WIRELESS",
  BOTH: "BOTH",
} as const satisfies Record<string, NonNullable<NetworkItem["connectionType"]>>;
export type ConnectionType = EnumUnion<typeof ConnectionTypeEnum>;

// アクセスレベル
export const AccessLevelEnum = {
  FULL_ACCESS: "FULL_ACCESS",
  READ_ONLY: "READ_ONLY",
  LIMITED: "LIMITED",
  NO_ACCESS: "NO_ACCESS",
} as const;
export type AccessLevel = EnumUnion<typeof AccessLevelEnum>;

// アクセスステータス
export const AccessStatusEnum = {
  ACTIVE: "ACTIVE",
  INACTIVE: "INACTIVE",
  SUSPENDED: "SUSPENDED",
  EXPIRED: "EXPIRED",
} as const;
export type AccessStatus = EnumUnion<typeof AccessStatusEnum>;

// サブジェクトタイプ
export const SubjectTypeEnum = {
  USER: "USER",
  DEVICE: "DEVICE",
} as const;
export type SubjectType = EnumUnion<typeof SubjectTypeEnum>;

// ネットワークステータス
export const NetworkStatusEnum = {
  CONNECTED: "CONNECTED",
  OUT_OF_SERVICE: "OUT_OF_SERVICE",
  ROAMING: "ROAMING",
  DISCONNECTED: "DISCONNECTED",
} as const;
export type NetworkStatus = EnumUnion<typeof NetworkStatusEnum>;

// SIMステータス
export const SimStatusEnum = {
  ACTIVE: "ACTIVE",
  INACTIVE: "INACTIVE",
  SUSPENDED: "SUSPENDED",
  UNKNOWN: "UNKNOWN",
} as const;
export type SimStatus = EnumUnion<typeof SimStatusEnum>;

// 宛先タイプ（デバイス/場所）
export const DestinationTypeEnum = {
  DEVICE: "DEVICE",
  PLACE: "PLACE",
} as const satisfies Record<
  string,
  NonNullable<NetworkItem["destinationType"]>
>;
export type DestinationType = EnumUnion<typeof DestinationTypeEnum>;

// ネットワークカテゴリ
export const NetworkCategory = {
  SHARED_INTERNAL: "SHARED_INTERNAL",
  PERSONAL_USE: "PERSONAL_USE",
  COMPANY_ASSET: "COMPANY_ASSET",
  CUSTOMER_ENVIRONMENT: "CUSTOMER_ENVIRONMENT",
  ALL: "ALL",
} as const satisfies Record<
  string,
  NonNullable<NetworkItem["networkCategory"]> | "ALL"
>;
export type NetworkCategory = EnumUnion<typeof NetworkCategory>;

// "ALL"を除外したネットワークカテゴリの型
export type NetworkCategoryWithoutAll = Exclude<NetworkCategory, "ALL">;

// フィルター用定数
export const NetworkTypeFilter = {
  ALL: "ALL",
  INTERNAL: "INTERNAL",
  EXTERNAL: "EXTERNAL",
  VPN: "VPN",
  DEDICATED_LINE: "DEDICATED_LINE",
  CLOUD: "CLOUD",
} as const;
export type NetworkTypeFilter = EnumUnion<typeof NetworkTypeFilter>;

export const ConnectionTypeFilter = {
  ALL: "ALL",
  WIRED: "WIRED",
  WIRELESS: "WIRELESS",
  BOTH: "BOTH",
} as const;
export type ConnectionTypeFilter = EnumUnion<typeof ConnectionTypeFilter>;

export const NetworkStatusFilter = {
  ALL: "ALL",
  CONNECTED: "CONNECTED",
  OUT_OF_SERVICE: "OUT_OF_SERVICE",
  ROAMING: "ROAMING",
  DISCONNECTED: "DISCONNECTED",
} as const;
export type NetworkStatusFilter = EnumUnion<typeof NetworkStatusFilter>;

export const AccessStatusFilter = {
  ALL: "ALL",
  ACTIVE: "ACTIVE",
  INACTIVE: "INACTIVE",
  SUSPENDED: "SUSPENDED",
  EXPIRED: "EXPIRED",
} as const;
export type AccessStatusFilter = EnumUnion<typeof AccessStatusFilter>;

// 型ガード関数
const networkTypeKeys: Set<NetworkType> = new Set(
  Object.values(NetworkTypeEnum),
);
export function isValidNetworkType(value: string): value is NetworkType {
  return networkTypeKeys.has(value as NetworkType);
}

const connectionTypeKeys: Set<ConnectionType> = new Set(
  Object.values(ConnectionTypeEnum),
);
export function isValidConnectionType(value: string): value is ConnectionType {
  return connectionTypeKeys.has(value as ConnectionType);
}

// ラベル変換用のヘルパー関数（国際化対応）
export function getNetworkTypeTranslationKey(networkType: string): string {
  const key = isValidNetworkType(networkType)
    ? networkType
    : NetworkTypeEnum.INTERNAL;
  return `networkTypeValues.${key}`;
}

export function getConnectionTypeTranslationKey(
  connectionType: string,
): string {
  const key = isValidConnectionType(connectionType)
    ? connectionType
    : ConnectionTypeEnum.WIRELESS;
  return `connectionTypeValues.${key}`;
}
